import { useEffect, useState } from 'react';
import { getSsrData } from '../libs/ssrData';
import {
  agruparDiasMag,
  masReciente,
  normalizarFilas,
  type DiaMag,
  type FilaMagDb,
  type MagSnapshot,
} from '../helpers/preciosMagHelper';

/**
 * Precios diarios oficiales del Mercado Agroganadero para /precios.
 *
 * 1) Estado inicial: el snapshot del build (en el render estático viene
 *    precargado vía ssrData, así Google y los bots leen el precio en el HTML).
 * 2) En el navegador: pide /mag-snapshot.json y después la base (RPC
 *    precios_mag_ultimos_dias, lectura pública) para mostrar el día más nuevo
 *    aunque el sitio no se haya vuelto a publicar. Sin conexión o sin datos,
 *    queda lo que había.
 */
export function usePreciosMag(): { dias: DiaMag[]; cargando: boolean } {
  const [filas, setFilas] = useState<FilaMagDb[]>(() => getSsrData<MagSnapshot>('mag')?.filas ?? []);
  const [cargando, setCargando] = useState(() => !getSsrData('mag'));

  useEffect(() => {
    let activo = true;
    const aplicar = (nuevas: FilaMagDb[]) => {
      if (activo && nuevas.length) setFilas((actuales) => masReciente(actuales, nuevas));
    };

    (async () => {
      try {
        if (typeof fetch === 'function') {
          const res = await fetch('/mag-snapshot.json', { cache: 'no-cache' });
          if (res.ok) {
            const snap = (await res.json()) as MagSnapshot;
            aplicar(normalizarFilas(snap.filas ?? []));
          }
        }
      } catch {
        /* sin snapshot: se intenta la base */
      }
      try {
        const { supabase } = await import('../libs/supabaseClient');
        const { data } = await supabase.rpc('precios_mag_ultimos_dias', {
          p_dias: 10,
        });
        if (Array.isArray(data)) aplicar(normalizarFilas(data));
      } catch {
        /* sin conexión o sin configuración: queda el snapshot */
      } finally {
        if (activo) setCargando(false);
      }
    })();

    return () => {
      activo = false;
    };
  }, []);

  return { dias: agruparDiasMag(filas), cargando };
}
